import { useEffect, useRef } from 'react';

export default function PhilosophyChapter04() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up, .fade-up-item, .scale-in').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const reasons = [
    {
      num: '①',
      title: '歯はもともと「前かがみ」に生えている',
      body: '歯は垂直ではなく、\n前方へ倒れる力を受けやすい向きで存在しています。',
    },
    {
      num: '②',
      title: '親知らずなど、後方からの圧力が加わる',
      body: '気づかないうちに、\n歯列全体が前へ押される力がかかり続けます。',
    },
    {
      num: '③',
      title: 'アーチが崩れるほど、悪化が加速する',
      body: '一部が乱れると、\nそこを起点に歯列全体が崩れやすくなります。',
    },
  ];

  return (
    <section ref={sectionRef} id="chapter5" className="py-20 px-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12 fade-up">
          <p className="text-lg font-bold mb-4" style={{ color: '#3E5F8F' }}>04</p>
          <h2 className="text-3xl md:text-4xl font-normal text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>
            10年後の自分に、選択肢を残す。
          </h2>
          <p className="text-xl text-gray-700 mb-6">矯正は「終わったあと」のほうが長い治療です。</p>
          <p className="text-base text-gray-700 leading-relaxed">
            「今すぐ始めるべきか、それとも様子を見るべきか」<br />
            多くの方が抱くこの迷いに対し、<br />
            私たちは「損得」ではなく、<br />
            10年後の口腔内がどう変わるかという視点でお答えします。
          </p>
        </div>

        <div className="mb-12 bg-white rounded-lg p-8 fade-up">
          <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>歯並びは、何もしなければ「崩れる力」が勝ります</h3>
          <p className="text-base text-gray-700 leading-relaxed mb-6">
            歯列は本来、アーチ(弓状)の形でバランスを保っています。<br />
            しかし、一度並びに乱れが生じると、<br />
            維持する力より、崩れる力のほうが強く働きます。
          </p>
          <p className="text-base text-gray-700 mb-6">これを、「アーチ効果の破綻」と呼びます。</p>
          <div className="scale-in mb-6">
            <img
              alt="アーチ効果"
              className="w-full rounded-lg"
              src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-1024x576_v-fs_webp_5787ba34-0713-4faa-841c-820f01d445af_small.webp"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <img alt="正常なアーチ" className="w-full rounded-lg fade-up-item" src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-1184x775_v-fs_webp_8c5124d0-a92b-4d9c-808f-692774dac03c_small.webp" />
            <img alt="崩れたアーチ" className="w-full rounded-lg fade-up-item" src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-1184x769_v-fs_webp_616c960f-4f85-4658-8108-829a12195c87_small.webp" />
          </div>
        </div>

        <div className="mb-12 bg-white rounded-lg p-8 fade-up">
          <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>なぜ時間が経つほど、難しくなるのか</h3>
          <div className="space-y-6">
            {reasons.map((r, i) => (
              <div key={i} className="border-l-4 pl-6 fade-up-item" style={{ borderColor: '#3E5F8F' }}>
                <p className="text-base font-bold text-gray-900 mb-2">{r.num} {r.title}</p>
                <p className="text-sm text-gray-700 whitespace-pre-line">{r.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12 fade-up">
          <p className="text-base text-gray-700 mb-4">その結果、</p>
          <div className="bg-white rounded-lg p-6 mb-6">
            <p className="text-sm text-gray-800 leading-relaxed">
              ・5年後、10年後には「動かす量」が増える<br />
              ・年齢とともに歯は動きにくくなる<br />
              ・場合によっては、抜歯が必要になる可能性が高まる<br />
              といった変化が起こり得ます
            </p>
          </div>
          <p className="text-base text-gray-700 leading-relaxed mb-6">
            ここで誤解してほしくないのは、<br />
            「今すぐやらないと手遅れになる」という話ではない、ということです。<br /><br />
            私たちが伝えたいのは、<br />
            「今の状態を正確に記録し、判断軸を持っておくこと」こそが、<br />
            将来の自分への贈り物になるということです。
          </p>
          <div className="bg-white rounded-lg p-6 mb-6">
            <p className="text-sm text-gray-800 leading-relaxed">
              ・今なら、非抜歯で整理できる可能性がある<br />
              ・今なら、動かす量が少なく済む<br />
              ・今なら、選択肢の幅が広い
            </p>
          </div>
          <p className="text-base text-gray-700">
            こうした違いが、<br />
            将来の負担や矯正治療の難易度に影響します。
          </p>
        </div>

        <div className="mb-12 scale-in">
          <img
            alt="時間経過による変化"
            className="w-full rounded-lg"
            src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-1344x686_v-fms_webp_f29b233f-efb6-415e-ab6e-7d4c43061f3c_small.webp"
          />
        </div>

        <div className="rounded-lg p-8 fade-up" style={{ backgroundColor: '#EEF3F9' }}>
          <h4 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP, serif' }}>まとめ</h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            矯正は、整えた瞬間よりも、その後の人生のほうが遥かに長い治療です。<br /><br />
            時間の経過とともに、治療の難易度や前提条件は少しずつ変わっていきます。<br /><br />
            「今やる場合」と「様子を見る場合」の見通しを、専門家と一緒に一度整理してみる。<br />
            それが、10年後に後悔しないための最善のステップです。
          </p>
        </div>
      </div>
    </section>
  );
}